import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable, tap} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {UrlBuilder} from "../item/url.item";
import {map} from "rxjs/operators";
import {SearchDeathRes} from "../dto/search/search-death.res";
import {DeathDto} from "../dto/death.dto";
import {VoidDto} from "../dto/void.dto";
import {AddressDto} from "../dto/address.dto";
import {InvoiceDto} from "../dto/document/invoice.dto";

@Injectable({providedIn: 'root'})
export class DeathRepo {

  private readonly deathRead = '/v1/org/{orgId}/death/{id}';
  private readonly deathWrite = '/v1/org/{orgId}/death';
  private readonly deathDelete = '/v1/org/{orgId}/death/{id}/delete';
  private readonly deathCreateInvoice = '/v1/org/{orgId}/death/{id}/invoice/debtor/{debtorId}';
  private readonly deathSearch = '/v1/org/{orgId}/death/search';
  private readonly placeOfDeathAllRead = '/v1/org/{orgId}/death/{id}/pod';
  private readonly placeOfDeathWrite = '/v1/org/{orgId}/death/{id}/pod';
  private readonly placeOfDeathDelete = '/v1/org/{orgId}/death/{id}/pod/{podId}/delete';

  private readonly http: HttpClient = inject(HttpClient);

  public getById(org: OrgDto, id: string): Observable<WrapperDto<DeathDto>> {
    const url = UrlBuilder.builder().addPath(this.deathRead)
      .addParam('orgId', org.id).addParam('id', id).build();
    return this.http.get<WrapperDto<DeathDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public update(org: OrgDto, death: DeathDto): Observable<WrapperDto<DeathDto>> {
    const url = UrlBuilder.builder().addPath(this.deathWrite).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<DeathDto>>(url.getUrl(), death, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public delete(org: OrgDto, id: string): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.deathDelete)
      .addParam('orgId', org.id).addParam('id', id).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createInvoice(org: OrgDto, id: string, debtorId: string): Observable<WrapperDto<InvoiceDto>> {
    const url = UrlBuilder.builder().addPath(this.deathCreateInvoice)
      .addParam('orgId', org.id).addParam('id', id).addParam('debtorId', debtorId).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Search
  // ##########################################################################

  public search(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchDeathRes>> {
    const url = UrlBuilder.builder().addPath(this.deathSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchDeathRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchDeathRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }

  // ##########################################################################
  // #### Address
  // ##########################################################################

  public getPlaceOfDeath(org: OrgDto, deathId: string): Observable<WrapperDto<AddressDto>> {
    const url = UrlBuilder.builder().addPath(this.placeOfDeathAllRead)
      .addParam('orgId', org.id).addParam('id', deathId).build();
    return this.http.get<WrapperDto<AddressDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updatePlaceOfDeath(org: OrgDto, deathId: string, address: AddressDto): Observable<WrapperDto<AddressDto>> {
    const url = UrlBuilder.builder().addPath(this.placeOfDeathWrite)
      .addParam('orgId', org.id).addParam('id', deathId).build();
    return this.http.post<WrapperDto<AddressDto>>(url.getUrl(), address, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deletePlaceOfDeath(org: OrgDto, deathId: string, addressId: string): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.placeOfDeathDelete)
      .addParam('orgId', org.id).addParam('id', deathId).addParam('podId', addressId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }
}
